import React, { Component, Fragment } from 'react';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Tabs, Tab } from 'react-bootstrap';
import { isValidForm } from '../../util/validationMethods';
import LoadingProgress from '../../components/utilComponents/LoadingProgress';
import CompanyInfo from '../../components/adminComponents/company/companyInfo/companyInfo';
import CompanyHistory from '../../components/adminComponents/company/companyHistory/companyHistory';
import Billing from '../../components/adminComponents/company/billing/billing';
import AccountAssign from '../../components/adminComponents/company/accountAssign/accountAssign';
import PaymentSettingInfo from '../../components/adminComponents/company/paymentSettingInfo/paymentSettingInfo';
import { getComapanyTypes } from '../../actions/adminAction/dropDownDataAction';
import {
	getCompany,
	clearCompany,
	companyInfoChange,
	saveCompany,
	deleteNoteToList,
	setPaymentInfoToEmpty,
	setCompanyNotesToList,
	deleteContactToList,
	setCompanyContactToList,
	isValidSubdomain,
	validateUser,
	changeCompanyActivateStatus
} from '../../actions/adminAction/companyActions';
import { getAccountListForDropDown } from '../../actions/adminAction/accountAction';
import { notify } from '../../util/notifications';
import { getLoginUserName, getCompanyId, getAccessLevelsByModule } from '../../util/customMethods';
import moment from 'moment';

// Constants
const PAGE_TITLE = 'Add New Company';
const COMPANY_TABS = {
	TB_COMPANY_INFO: 'TB_COMPANY_INFO',
	TB_COMPANY_BILLING: 'TB_COMPANY_BILLING',
	TB_ACCOUNT_ASSIGN: 'TB_ACCOUNT_ASSIGN',
	TB_Payment_Setting: 'TB_Payment_Setting',
	TB_COMPANY_HISTORY: 'TB_COMPANY_HISTORY'
};

const LOADING_MESSAGES = {
	LOADING: 'Loading company information...',
	LOADED: 'Company information loaded!',
	VALIDATING_DOMAIN: 'Validating domain...',
	DOMAIN_VALIDATED: 'Domain validation complete!',
	VALIDATING_EMAIL: 'Validating email...',
	EMAIL_VALIDATED: 'Email validation complete!',
	VALIDATING_USER: 'Validating user...',
	USER_VALIDATED: 'User validation complete!',
	SAVING: 'Saving company information...',
	SAVED: 'Company saved successfully! Redirecting...',
	SAVING_NOTE: 'Saving note...',
	NOTE_SAVED: 'Note saved successfully!',
	DELETING_NOTE: 'Deleting note...',
	NOTE_DELETED: 'Note deleted successfully!',
	SAVING_CONTACT: 'Saving contact...',
	CONTACT_SAVED: 'Contact saved successfully!',
	DELETING_CONTACT: 'Deleting contact...',
	CONTACT_DELETED: 'Contact deleted successfully!',
	UPDATING_STATUS: 'Updating company status...',
	STATUS_UPDATED: 'Status updated successfully!'
};

const PROGRESS_TIMINGS = {
	PHASE_1: 200,
	PHASE_2: 400,
	PHASE_3: 600,
	COMPLETE: 800,
	REDIRECT: 1000
};

class CompanyRecord extends Component {
	constructor(props) {
		super(props);
		this.state = {
			tabKey: COMPANY_TABS.TB_COMPANY_INFO,
			cid: 0,
			isDomainValid: true,
			isEmailExits: true,
			isUserExits: true,
			isLoading: false,
			loadingMessage: '',
			loadingProgress: 0
		};

		// Use refs instead of module-level variable
		this.formRefs = {
			frmCompanyInfo: null,
			frmBilling: null,
			frmAccount: null,
			frmDefaultAddress: null,
			frmPaymentSetting: null
		};

		// Bind methods once
		this.handleTabSelect = this.handleTabSelect.bind(this);
	}

	componentDidMount() {
		const queryValues = queryString.parse(this.props.location.search);
		const cid = parseInt(queryValues.cid) || 0;
		
		this.setState({ cid }, () => {
			this.initializeComponent(cid);
		});
	}

	componentWillUnmount() {
		this.props.dispatch(setPaymentInfoToEmpty());
		// Clear any pending timeouts
		if (this.progressTimeout) clearTimeout(this.progressTimeout);
		if (this.resetTimeout) clearTimeout(this.resetTimeout);
	}

	// Consolidated initialization
	initializeComponent = async (cid) => {
		await this.getAccountList();
		await this.initCompanyForm(cid);
		this.props.dispatch(setPaymentInfoToEmpty());
	};

	// Optimized loading state management
	setLoadingState = (isLoading, message = '', progress = 0) => {
		if (this._unmounted) return;
		
		this.setState({
			isLoading,
			loadingMessage: message,
			loadingProgress: progress
		});
	};

	simulateProgress = () => {
		return new Promise((resolve) => {
			this.setLoadingState(true, this.state.loadingMessage, 25);

			this.progressTimeout = setTimeout(() => {
				this.setLoadingState(true, this.state.loadingMessage, 50);
				
				setTimeout(() => {
					this.setLoadingState(true, this.state.loadingMessage, 75);
					
					setTimeout(() => {
						this.setLoadingState(true, this.state.loadingMessage, 100);
						resolve();
					}, PROGRESS_TIMINGS.PHASE_1);
				}, PROGRESS_TIMINGS.PHASE_1);
			}, PROGRESS_TIMINGS.PHASE_1);
		});
	};

	resetLoading = (delay = 0) => {
		if (delay > 0) {
			this.resetTimeout = setTimeout(() => {
				this.setLoadingState(false, '', 0);
			}, delay);
		} else {
			this.setLoadingState(false, '', 0);
		}
	};

	// Generic async operation wrapper
	executeAsyncOperation = async (operation, loadingMsg, successMsg, errorMsg) => {
		this.setLoadingState(true, loadingMsg, 50);
		
		try {
			const result = await operation();
			
			if (successMsg) {
				this.setLoadingState(true, successMsg, 100);
				this.resetLoading(PROGRESS_TIMINGS.COMPLETE);
			} else {
				this.resetLoading();
			}
			
			return result;
		} catch (error) {
			this.resetLoading();
			if (errorMsg) {
				notify.createNotification(notify.notifyType.Error, errorMsg, this.props.refNotify);
			}
			throw error;
		}
	};

	initCompanyForm = async (id) => {
		this.setLoadingState(true, LOADING_MESSAGES.LOADING, 30);

		try {
			await this.props.dispatch(getComapanyTypes());
			
			this.props.dispatch(clearCompany());
			
			if (id !== 0) {
				await this.props.dispatch(getCompany(id));
			}

			this.setLoadingState(true, LOADING_MESSAGES.LOADED, 100);
			this.resetLoading(PROGRESS_TIMINGS.COMPLETE);
		} catch (error) {
			this.resetLoading();
			notify.createNotification(
				notify.notifyType.Error,
				'Failed to load company information.',
				this.props.refNotify
			);
		}
	};

	getAccountList = async () => {
		await this.props.dispatch(getAccountListForDropDown());
	};

	validateDomain = async (dname) => {
		const status = await this.executeAsyncOperation(
			() => isValidSubdomain(dname),
			LOADING_MESSAGES.VALIDATING_DOMAIN,
			LOADING_MESSAGES.DOMAIN_VALIDATED,
			null
		);
		this.setState({ isDomainValid: status });
	};

	emailExits = async (email) => {
		const status = await this.executeAsyncOperation(
			() => validateUser(0, email, 'email'),
			LOADING_MESSAGES.VALIDATING_EMAIL,
			LOADING_MESSAGES.EMAIL_VALIDATED,
			null
		);
		this.setState({ isEmailExits: status });
	};

	validateUserById = async (id) => {
		const status = await this.executeAsyncOperation(
			() => validateUser(getCompanyId(), id, 'userId'),
			LOADING_MESSAGES.VALIDATING_USER,
			LOADING_MESSAGES.USER_VALIDATED,
			null
		);
		this.setState({ isUserExits: status });
	};

	validateForms = () => {
		const { frmCompanyInfo, frmBilling } = this.formRefs;

		// Validate company info
		if (frmCompanyInfo?.validateAll) {
			frmCompanyInfo.validateAll();
			if (!isValidForm(frmCompanyInfo)) {
				this.setState({ tabKey: COMPANY_TABS.TB_COMPANY_INFO });
				notify.createNotification(
					notify.notifyType.Error,
					'Please complete the Company Information section.',
					this.props.refNotify
				);
				return false;
			}
		}

		// Validate billing
		if (frmBilling?.validateAll) {
			frmBilling.validateAll();
			if (!isValidForm(frmBilling)) {
				this.setState({ tabKey: COMPANY_TABS.TB_COMPANY_BILLING });
				notify.createNotification(
					notify.notifyType.Error,
					'Please complete the Billing section.',
					this.props.refNotify
				);
				return false;
			}
		}

		return true;
	};

	onCompanySave = async () => {
		if (!this.validateForms()) return;

		this.setLoadingState(true, LOADING_MESSAGES.SAVING, 0);

		await this.simulateProgress();

		try {
			const resp = await saveCompany(this.props.companyRec);
			
			if (resp.statusCode === 200) {
				this.setLoadingState(true, LOADING_MESSAGES.SAVED, 100);
				notify.createNotification(notify.notifyType.Success, resp.statusMsg, this.props.refNotify);
				
				this.resetTimeout = setTimeout(() => {
					this.resetLoading();
					this.props.history.push('/admin/company');
				}, PROGRESS_TIMINGS.REDIRECT);
			} else {
				this.resetLoading();
				notify.createNotification(notify.notifyType.Error, resp.statusMsg, this.props.refNotify);
			}
		} catch (error) {
			this.resetLoading();
			notify.createNotification(
				notify.notifyType.Error,
				'An error occurred while saving the company.',
				this.props.refNotify
			);
		}
	};

	// Consolidated note/contact operations
	handleNoteSubmit = async (notesObj, index, type) => {
		return this.executeAsyncOperation(
			async () => {
				const enhancedNote = {
					...notesObj,
					createdDate: moment().format('YYYY-MM-DD'),
					createdTime: moment().format('HH:mm:ss'),
					idCompany: this.state.cid,
					userId: getLoginUserName()
				};
				
				const resp = await this.props.dispatch(setCompanyNotesToList(enhancedNote, index, type));
				const notificationType = resp.statusCode === 200 ? notify.notifyType.Success : notify.notifyType.Error;
				notify.createNotification(notificationType, resp.statusMsg, this.props.refNotify);
				
				return resp;
			},
			LOADING_MESSAGES.SAVING_NOTE,
			LOADING_MESSAGES.NOTE_SAVED,
			'Failed to save note.'
		);
	};

	handleNoteDelete = async (index, type) => {
		return this.executeAsyncOperation(
			async () => {
				const resp = await this.props.dispatch(deleteNoteToList(this.state.cid, index, type));
				const notificationType = resp.statusCode === 200 ? notify.notifyType.Success : notify.notifyType.Error;
				notify.createNotification(notificationType, resp.statusMsg, this.props.refNotify);
				
				return resp;
			},
			LOADING_MESSAGES.DELETING_NOTE,
			LOADING_MESSAGES.NOTE_DELETED,
			'Failed to delete note.'
		);
	};

	handleContactSubmit = async (contactObj, index, type) => {
		return this.executeAsyncOperation(
			async () => {
				const enhancedContact = {
					...contactObj,
					companyId: this.state.cid
				};
				
				const resp = await this.props.dispatch(setCompanyContactToList(enhancedContact, index, type));
				const notificationType = resp.statusCode === 200 ? notify.notifyType.Success : notify.notifyType.Error;
				notify.createNotification(notificationType, resp.statusMsg, this.props.refNotify);
				
				return true;
			},
			LOADING_MESSAGES.SAVING_CONTACT,
			LOADING_MESSAGES.CONTACT_SAVED,
			'Failed to save contact.'
		);
	};

	handleContactDelete = async (index, type) => {
		return this.executeAsyncOperation(
			async () => {
				const resp = await this.props.dispatch(deleteContactToList(this.state.cid, index, type));
				const notificationType = resp.statusCode === 200 ? notify.notifyType.Success : notify.notifyType.Error;
				notify.createNotification(notificationType, resp.statusMsg, this.props.refNotify);
				
				return resp;
			},
			LOADING_MESSAGES.DELETING_CONTACT,
			LOADING_MESSAGES.CONTACT_DELETED,
			'Failed to delete contact.'
		);
	};

	handleChangeActivateStatus = async (companyId, isActive) => {
		return this.executeAsyncOperation(
			() => changeCompanyActivateStatus(companyId, isActive),
			LOADING_MESSAGES.UPDATING_STATUS,
			LOADING_MESSAGES.STATUS_UPDATED,
			'Failed to update company status.'
		);
	};

	handleFieldChange = (keyName, value) => {
		this.props.dispatch(companyInfoChange(keyName, value));
	};

	handleAccountAssignChange = async (keyName, value) => {
		await this.props.dispatch(companyInfoChange(keyName, value));
		await this.props.dispatch(companyInfoChange('isAccountAssignChange', true));
	};

	handleTabSelect = (key) => {
		this.setState({ tabKey: key });
	};

	checkViewAccess = () => {
		const accessLevel = getAccessLevelsByModule('Company');
		if (!accessLevel.basicAccess.view) {
			this.props.history.push('/admin/notfound');
		}
		return accessLevel;
	};

	renderTabContent = (accessLevelObj) => {
		const { companyRec, accountsList, states, companyType, refNotify } = this.props;
		const { cid, isLoading, isDomainValid, isEmailExits, isUserExits } = this.state;
		const canEdit = accessLevelObj.basicAccess.edit || false;

		const commonProps = {
			canEdit,
			disabled: isLoading,
			onSaveCompany: this.onCompanySave,
			cid
		};

		return (
			<Tabs 
				id="tab-company" 
				className="tab" 
				activeKey={this.state.tabKey} 
				onSelect={this.handleTabSelect}
			>
				<Tab
					eventKey={COMPANY_TABS.TB_COMPANY_INFO}
					title={<span><i className="fa fa-info-circle" style={{ display: 'block', textAlign: 'center' }} />Company Information</span>}
				>
					<CompanyInfo
						{...commonProps}
						accounts={accountsList || []}
						company={companyRec || {}}
						states={states || []}
						companyType={companyType || []}
						initCallBack={(frm) => { this.formRefs.frmCompanyInfo = frm; }}
						onCompanyBasicInfoFieldsChange={this.handleFieldChange}
						onSubmitBasicInfo={() => this.setState({ tabKey: COMPANY_TABS.TB_COMPANY_BILLING })}
						validateDomain={this.validateDomain}
						emailExits={this.emailExits}
						isEmailExits={isEmailExits}
						isDomainValid={isDomainValid}
						validateUser={this.validateUserById}
						isUserExits={isUserExits}
						onChangeActivateStatus={this.handleChangeActivateStatus}
						onCompanyNotesSubmit={(e, notesObj, index) => this.handleNoteSubmit(notesObj, index, 'COMPANY')}
						onCompanyNotesDelete={(index) => this.handleNoteDelete(index, 'COMPANY')}
						onCompanyContactSubmit={(e, contactObj, index) => this.handleContactSubmit(contactObj, index, 'COMPANY')}
						onCompanyContactDelete={(index) => this.handleContactDelete(index, 'COMPANY')}
						refNotify={refNotify}
					/>
				</Tab>

				{companyRec?.companyType !== "Retail" && (
					<Tab
						eventKey={COMPANY_TABS.TB_ACCOUNT_ASSIGN}
						title={<span><i className="fa fa-money" style={{ display: 'block', textAlign: 'center' }} />Assign Accounts</span>}
					>
						<AccountAssign
							{...commonProps}
							accountList={[...(accountsList || [])]}
							selectedAccounts={companyRec.companyAccountMappings || []}
							onCompanyBasicInfoFieldsChange={this.handleAccountAssignChange}
							onSubmitAccountAssign={() => {
								const count = (companyRec.companyAccountMappings || []).length;
								if (count > 0) {
									this.setState({ tabKey: COMPANY_TABS.TB_COMPANY_BILLING });
								} else {
									notify.createNotification(notify.notifyType.Error, 'Select at least one account.', refNotify);
								}
							}}
						/>
					</Tab>
				)}

				<Tab
					eventKey={COMPANY_TABS.TB_COMPANY_BILLING}
					title={<span><i className="fa fa-money" style={{ display: 'block', textAlign: 'center' }} />Billing</span>}
				>
					<Billing
						{...commonProps}
						company={companyRec || {}}
						states={states || []}
						initCallBack={(frm) => { this.formRefs.frmBilling = frm; }}
						onCompanyBillingInfoFieldsChange={this.handleFieldChange}
						onCompanyNotesSubmit={(e, notesObj, index) => this.handleNoteSubmit(notesObj, index, 'BILLING')}
						onCompanyNotesDelete={(index) => this.handleNoteDelete(index, 'BILLING')}
						onCompanyContactSubmit={(e, contactObj, index) => this.handleContactSubmit(contactObj, index, 'BILLING')}
						onSubmitBillingInfo={() => this.setState({ tabKey: COMPANY_TABS.TB_Payment_Setting })}
						onCompanyContactDelete={(index) => this.handleContactDelete(index, 'BILLING')}
					/>
				</Tab>

				<Tab
					eventKey={COMPANY_TABS.TB_Payment_Setting}
					title={<span><i className="fa fa-money" style={{ display: 'block', textAlign: 'center' }} />Payment Settings</span>}
				>
					<PaymentSettingInfo
						{...commonProps}
						states={states || []}
						company={companyRec || {}}
						paymentObj={this.props.paymentObj || {}}
						initCallBack={(frm) => { this.formRefs.frmPaymentSetting = frm; }}
						setPaymentInfo={this.handleFieldChange}
					/>
				</Tab>

				{companyRec.idCompany > 0 && (
					<Tab
						eventKey={COMPANY_TABS.TB_COMPANY_HISTORY}
						title={<span><i className="fa fa-history" style={{ display: 'block', textAlign: 'center' }} />Company History</span>}
					>
						<CompanyHistory canEdit={canEdit} company={companyRec || {}} />
					</Tab>
				)}
			</Tabs>
		);
	};

	render() {
		const { companyRec } = this.props;
		const { cid, isLoading, loadingMessage, loadingProgress } = this.state;
		const accessLevelObj = this.checkViewAccess();
		
		document.title = cid <= 0 ? PAGE_TITLE : `Edit ${companyRec.name}`;

		return (
			<Fragment>
				<div className="page-header">
					<div className="row mb-10">
						<div className="col-md-12 col-sm-12">
							<div className="title">
								<h4>
									<i className="fa fa-building-o" /> {cid <= 0 ? PAGE_TITLE : `Edit ${companyRec.name}`}
								</h4>
							</div>
						</div>
					</div>
				</div>

				<LoadingProgress
					isLoading={isLoading}
					message={loadingMessage}
					progress={loadingProgress}
				/>

				<div 
					className="pd-10 bg-white border-radius-4 box-shadow container-box"
					style={{
						opacity: isLoading ? 0.7 : 1,
						pointerEvents: isLoading ? 'none' : 'auto',
						transition: 'opacity 0.3s ease'
					}}
				>
					{this.renderTabContent(accessLevelObj)}
				</div>
			</Fragment>
		);
	}
}

const mapStateToProps = (state) => ({
	refNotify: state.refNotify,
	companyRec: state.companies.companyRec,
	accountsList: state.accountList.accountsDropDownList,
	states: state.states.stateList,
	companyType: state.companyTypes.companyType
});

export default withRouter(connect(mapStateToProps)(CompanyRecord));